import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { FiPlay, FiSettings } from 'react-icons/fi';
import { db } from '../../services/supabase';
import toast from 'react-hot-toast';
import BullseyeIcon from '../common/icons/BullseyeIcon';
import RandomDrawIcon from '../common/icons/RandomDrawIcon';
import TrophyIcon from '../common/icons/TrophyIcon';
import WarningIcon from '../common/icons/WarningIcon';
import { useHasRole } from '../../hooks/useHasRole';

const DRAW_HISTORY_REFRESH_MS = 1000;
const ELIGIBLE_TICKETS_REFRESH_MS = 2000;

const DRAW_GROUP_PLAN = [
  { size: 3, title: '1. skupina' },
  { size: 10, title: '2. skupina' },
  { size: 6, title: '3. skupina' },
  { size: 6, title: '4. skupina' },
  { size: 6, title: '5. skupina' },
  { size: 6, title: '6. skupina' },
  { size: 1, title: 'Veľká cena #3' },
  { size: 1, title: 'Veľká cena #2' },
  { size: 1, title: 'Veľká cena #1' },
];

const getNextGroupInfo = (drawsDone) => {
  let consumed = 0;
  for (let index = 0; index < DRAW_GROUP_PLAN.length; index += 1) {
    const group = DRAW_GROUP_PLAN[index];
    const groupEnd = consumed + group.size;
    if (drawsDone < groupEnd) {
      return {
        ...group,
        index,
        remainingInGroup: groupEnd - drawsDone,
      };
    }
    consumed = groupEnd;
  }
  return null;
};

const buildGroupedHistory = (drawHistory) => {
  const sorted = [...drawHistory].sort((a, b) => Number(b.drawNumber || 0) - Number(a.drawNumber || 0));
  let cursor = 0;

  return DRAW_GROUP_PLAN.map((group, index) => {
    const draws = sorted.slice(cursor, cursor + group.size);
    cursor += group.size;
    return {
      index,
      title: group.title,
      size: group.size,
      draws,
    };
  }).filter((group) => group.draws.length > 0);
};

const getPreviewSlotsCount = (group) => {
  if (!group) return 1;
  if (group.title?.includes('Veľká cena')) return 1;
  return Math.max(Number(group.size || 0), 1);
};

const isGrandPrizeGroup = (group) =>
  Boolean(group?.title?.includes('Veľká cena')) || Number(group?.size || 0) === 1;

const getStepDelayMs = (group) => (isGrandPrizeGroup(group) ? 3200 : 2500);

const getStartDelayMs = (group) => (isGrandPrizeGroup(group) ? 1500 : 2200);

const getConfettiConfig = (group) => {
  if (isGrandPrizeGroup(group)) {
    return { particles: 70, durationMs: 1600, animationMs: 1500 };
  }
  return { particles: 30, durationMs: 1200, animationMs: 1100 };
};

const ensureFireworksStyles = () => {
  const styleId = 'fireworks-style';
  if (document.getElementById(styleId)) return;

  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    .fireworks-overlay {
      position: fixed;
      inset: 0;
      pointer-events: none;
      overflow: hidden;
      z-index: 10000;
    }
    .firework-burst {
      position: absolute;
      width: 6px;
      height: 6px;
      border-radius: 999px;
      box-shadow: 0 0 16px rgba(255, 220, 120, 0.95);
      animation: firework-burst 1200ms ease-out forwards;
    }
    @keyframes firework-burst {
      0% {
        transform: translate3d(0, 0, 0) scale(0.4);
        opacity: 1;
      }
      100% {
        transform: translate3d(var(--dx), var(--dy), 0) scale(1.2);
        opacity: 0;
      }
    }
  `;
  document.head.appendChild(style);
};

const DrawInterface = () => {
  const [isDrawing, setIsDrawing] = useState(false);
  const [isStagingResults, setIsStagingResults] = useState(false);
  const [activeGroup, setActiveGroup] = useState(null);
  const [liveGroupPreview, setLiveGroupPreview] = useState({ title: null, draws: [] });
  const [completedGroupPreview, setCompletedGroupPreview] = useState(null);
  const [historySnapshot, setHistorySnapshot] = useState(null);
  const [publicVisibleHistory, setPublicVisibleHistory] = useState([]);
  const [ticketRotateTick, setTicketRotateTick] = useState(0);
  const finaleFireworksIntervalRef = useRef(null);
  const publicHistoryTimerRef = useRef(null);
  const pendingPublicHistoryRef = useRef([]);
  const queryClient = useQueryClient();
  const { hasRole: isAdmin } = useHasRole(['admin']);

  // Fetch draw history and eligible tickets
  const { data: drawHistory = [] } = useQuery({
    queryKey: ['draw-history'],
    queryFn: async () => {
      const result = await db.draws.getHistory();
      return result ?? [];
    },
    refetchInterval: DRAW_HISTORY_REFRESH_MS,
    refetchIntervalInBackground: true,
  });

  const { data: eligibleTickets = [] } = useQuery({
    queryKey: ['eligible-tickets'],
    queryFn: async () => {
      const result = await db.tickets.getEligible();
      return result ?? [];
    },
    refetchInterval: ELIGIBLE_TICKETS_REFRESH_MS,
    refetchIntervalInBackground: true,
  });

  const groupedHistory = useMemo(() => buildGroupedHistory(drawHistory), [drawHistory]);
  const historyForVisibleGroups = isAdmin ? (historySnapshot ?? drawHistory) : publicVisibleHistory;
  const visibleGroupedHistory = useMemo(
    () => buildGroupedHistory(historyForVisibleGroups),
    [historyForVisibleGroups]
  );
  const displayGroup = completedGroupPreview;
  const isDisplayGroupInProgress = Boolean(displayGroup && displayGroup.draws.length < displayGroup.size);
  const nextGroup = useMemo(() => getNextGroupInfo(drawHistory.length), [drawHistory.length]);
  const rotatingEligibleTickets = useMemo(
    () => (eligibleTickets ?? []).map((ticket) => ticket.ticket_number).filter(Boolean),
    [eligibleTickets]
  );
  const previewGroup = useMemo(() => {
    if (liveGroupPreview.title) {
      return {
        title: liveGroupPreview.title,
        draws: liveGroupPreview.draws,
        size: activeGroup?.size ?? Math.max(liveGroupPreview.draws.length, 3),
      };
    }
    if (completedGroupPreview?.title) {
      return completedGroupPreview;
    }
    if (nextGroup) {
      const existing = groupedHistory.find((group) => group.title === nextGroup.title);
      return {
        title: nextGroup.title,
        draws: existing?.draws ?? [],
        size: nextGroup.size,
      };
    }
    const active = groupedHistory.find((group) => group.draws.length < group.size) ?? groupedHistory[0];
    if (active) return active;
    return { title: 'Skupina', draws: [], size: 1 };
  }, [groupedHistory, liveGroupPreview, activeGroup, nextGroup, completedGroupPreview]);

  useEffect(() => {
    if (rotatingEligibleTickets.length === 0) return;
    const interval = setInterval(() => {
      setTicketRotateTick((prev) => prev + 1);
    }, 35);
    return () => clearInterval(interval);
  }, [rotatingEligibleTickets.length]);

  useEffect(() => {
    if (isAdmin) {
      if (publicHistoryTimerRef.current) {
        clearTimeout(publicHistoryTimerRef.current);
        publicHistoryTimerRef.current = null;
      }
      setPublicVisibleHistory(drawHistory);
      pendingPublicHistoryRef.current = drawHistory;
      return;
    }

    if (drawHistory.length <= publicVisibleHistory.length) {
      setPublicVisibleHistory(drawHistory);
      pendingPublicHistoryRef.current = drawHistory;
      return;
    }

    const delta = drawHistory.length - publicVisibleHistory.length;
    const inferredGroup = {
      size: delta,
      title: delta === 1 ? 'Veľká cena' : 'Skupina',
    };
    const publishDelayMs = getStartDelayMs(inferredGroup) + (delta * getStepDelayMs(inferredGroup)) + 250;

    pendingPublicHistoryRef.current = drawHistory;
    if (publicHistoryTimerRef.current) {
      clearTimeout(publicHistoryTimerRef.current);
    }

    publicHistoryTimerRef.current = setTimeout(() => {
      setPublicVisibleHistory(pendingPublicHistoryRef.current);
      publicHistoryTimerRef.current = null;
    }, publishDelayMs);
  }, [drawHistory, isAdmin, publicVisibleHistory.length]);

  // Draw mutation - next group
  const drawMutation = useMutation({
    mutationFn: () => db.draws.performNextGroup(),
    onSuccess: async (winners, groupMeta) => {
      const orderedWinners = [...winners].sort(
        (a, b) => Number(b.drawNumber ?? 0) - Number(a.drawNumber ?? 0)
      );
      const resolvedGroupSize = groupMeta?.size ?? activeGroup?.size ?? orderedWinners.length;
      setIsStagingResults(true);
      setActiveGroup((prev) => {
        if (groupMeta?.title) {
          return { title: groupMeta.title, size: resolvedGroupSize };
        }
        return prev ? { ...prev, size: resolvedGroupSize } : prev;
      });
      const timingGroup = groupMeta?.title
        ? { title: groupMeta.title, size: resolvedGroupSize }
        : activeGroup;
      const stepDelayMs = getStepDelayMs(timingGroup);

      orderedWinners.forEach((winner, index) => {
        setTimeout(() => {
          setLiveGroupPreview((prev) => ({
            ...prev,
            draws: [...prev.draws, winner],
          }));
        }, (index + 1) * stepDelayMs);
      });

      const revealDuration = orderedWinners.length * stepDelayMs + 200;
      setTimeout(async () => {
        setIsStagingResults(false);
        setIsDrawing(false);
        const finishedGroup = groupMeta?.title
          ? { title: groupMeta.title, size: resolvedGroupSize, draws: orderedWinners }
          : null;
        setActiveGroup(null);

        await queryClient.invalidateQueries({ queryKey: ['draw-history'] });
        await queryClient.invalidateQueries({ queryKey: ['eligible-tickets'] });
        await queryClient.invalidateQueries({ queryKey: ['admin-stats'] });
        await queryClient.refetchQueries({ queryKey: ['draw-history'] });

        setLiveGroupPreview({ title: null, draws: [] });
        setHistorySnapshot(null);
        if (finishedGroup) {
          setCompletedGroupPreview(finishedGroup);
        }
        showConfetti(finishedGroup);
        if (finishedGroup?.title === 'Veľká cena #1') {
          startFinaleFireworks();
        } else if (isGrandPrizeGroup(finishedGroup)) {
          showFireworks();
        }

        toast.success(`Skupina vyžrebovaná: ${winners.length} víťazov`, {
          duration: 5000,
          icon: <TrophyIcon className="text-success-300" aria-hidden="true" />,
        });
      }, revealDuration);
    },
    onError: (error) => {
      toast.error(`Chyba pri žrebovaní: ${error.message}`, {
        icon: <WarningIcon className="text-error-400" aria-hidden="true" />,
      });
      setActiveGroup(null);
      setLiveGroupPreview({ title: null, draws: [] });
      setIsStagingResults(false);
      setIsDrawing(false);
      setHistorySnapshot(null);
      stopFinaleFireworks();
    }
  });

  const handleDrawGroup = useCallback(async () => {
    if (!isAdmin || isDrawing || eligibleTickets.length === 0 || !nextGroup) return;
    
    const selectedGroup = { title: nextGroup.title, size: nextGroup.size };
    setIsDrawing(true);
    stopFinaleFireworks();
    setCompletedGroupPreview(null);
    setHistorySnapshot(drawHistory);
    setActiveGroup(selectedGroup);
    setLiveGroupPreview({ title: selectedGroup.title, draws: [] });
    
    // Animation delay
    const startDelayMs = getStartDelayMs(selectedGroup);
    setTimeout(() => {
      drawMutation.mutate(selectedGroup);
    }, startDelayMs);
  }, [isAdmin, isDrawing, eligibleTickets.length, drawMutation, nextGroup, drawHistory]);

  useEffect(() => {
    return () => {
      if (publicHistoryTimerRef.current) {
        clearTimeout(publicHistoryTimerRef.current);
        publicHistoryTimerRef.current = null;
      }
      stopFinaleFireworks();
    };
  }, []);

  // Listen for Arduino button press events from SettingsForm
  useEffect(() => {
    const handleArduinoButtonPress = () => {
      if (!isDrawing && eligibleTickets.length > 0) {
        handleDrawGroup();
      }
    };

    window.addEventListener('arduino:button-pressed', handleArduinoButtonPress);
    return () => {
      window.removeEventListener('arduino:button-pressed', handleArduinoButtonPress);
    };
  }, [isDrawing, eligibleTickets.length, handleDrawGroup]);

  const showConfetti = (group) => {
    const { particles, durationMs, animationMs } = getConfettiConfig(group);
    const styleId = 'confetti-style';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        .confetti-explosion {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          pointer-events: none;
          overflow: hidden;
          z-index: 9999;
        }
        .confetti-particle {
          position: absolute;
          top: -10px;
          width: 8px;
          height: 14px;
          border-radius: 2px;
          opacity: 0;
          animation: confetti-fall 1.5s forwards ease-in;
        }
        .confetti-particle-star {
          width: 18px;
          height: 18px;
          border-radius: 0;
          box-shadow: 0 0 12px rgba(235, 185, 74, 0.95);
          filter: brightness(1.2);
          clip-path: polygon(
            50% 0%,
            61% 35%,
            98% 35%,
            68% 57%,
            79% 91%,
            50% 70%,
            21% 91%,
            32% 57%,
            2% 35%,
            39% 35%
          );
        }
        @keyframes confetti-fall {
          0% {
            transform: translate3d(0, -10px, 0) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translate3d(0, 120vh, 0) rotate(360deg);
            opacity: 0;
          }
        }
      `;
      document.head.appendChild(style);
    }

    const colors = ['#F97316', '#6366F1', '#FACC15', '#F43F5E', '#34D399'];
    const goldStars = ['#D4AF37', '#E5C35A', '#F2D27A'];
    const confetti = document.createElement('div');
    confetti.className = 'confetti-explosion';

    for (let i = 0; i < particles; i += 1) {
      const particle = document.createElement('span');
      particle.className = 'confetti-particle';
      const isStar = Math.random() < 0.75;
      if (isStar) {
        particle.classList.add('confetti-particle-star');
        particle.style.backgroundColor = goldStars[i % goldStars.length];
      } else {
        particle.style.backgroundColor = colors[i % colors.length];
      }
      particle.style.left = `${Math.random() * 100}%`;
      particle.style.animationDelay = `${Math.random() * 0.5}s`;
      particle.style.animationDuration = `${animationMs}ms`;
      confetti.appendChild(particle);
    }

    document.body.appendChild(confetti);

    setTimeout(() => {
      if (confetti.parentNode) {
        confetti.parentNode.removeChild(confetti);
      }
    }, durationMs);
  };

  const showFireworks = (variant = 'normal') => {
    ensureFireworksStyles();
    const overlay = document.createElement('div');
    overlay.className = 'fireworks-overlay';
    const colors = ['#D4AF37', '#F97316', '#FACC15', '#F8FAFC'];
    const isFinale = variant === 'finale';
    const bursts = isFinale ? 8 : 4;
    const particlesPerBurst = isFinale ? 40 : 24;
    const minDistance = isFinale ? 90 : 60;
    const maxDistance = isFinale ? 190 : 120;

    for (let b = 0; b < bursts; b += 1) {
      const cx = 15 + Math.random() * 70;
      const cy = 15 + Math.random() * 40;
      for (let i = 0; i < particlesPerBurst; i += 1) {
        const particle = document.createElement('span');
        const angle = (Math.PI * 2 * i) / particlesPerBurst;
        const distance = minDistance + Math.random() * maxDistance;
        particle.className = 'firework-burst';
        if (isFinale) {
          particle.style.width = '9px';
          particle.style.height = '9px';
          particle.style.boxShadow = '0 0 22px rgba(255, 220, 120, 1)';
        }
        particle.style.left = `${cx}%`;
        particle.style.top = `${cy}%`;
        particle.style.backgroundColor = colors[(i + b) % colors.length];
        particle.style.setProperty('--dx', `${Math.cos(angle) * distance}px`);
        particle.style.setProperty('--dy', `${Math.sin(angle) * distance}px`);
        particle.style.animationDelay = `${b * 180}ms`;
        overlay.appendChild(particle);
      }
    }

    document.body.appendChild(overlay);
    setTimeout(() => {
      if (overlay.parentNode) {
        overlay.parentNode.removeChild(overlay);
      }
    }, 1800);
  };

  const stopFinaleFireworks = () => {
    if (finaleFireworksIntervalRef.current) {
      clearInterval(finaleFireworksIntervalRef.current);
      finaleFireworksIntervalRef.current = null;
    }
  };

  const startFinaleFireworks = () => {
    stopFinaleFireworks();
    showFireworks('finale');
    finaleFireworksIntervalRef.current = setInterval(() => {
      showFireworks('finale');
    }, 700);
  };

  return (
    <div className="space-y-8 text-slate-100">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-4xl font-bold mb-3 flex items-center justify-center gap-3">
          <BullseyeIcon className="text-primary-300 animate-draw-glow" aria-hidden="true" />
          <span>Žrebovanie</span>
        </h1>
      </div>

      {isAdmin && (
        <div className="rounded-2xl p-8 text-center text-slate-100 border border-primary-500/40 bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900 shadow-glow">
          <AnimatePresence mode="wait">
            {isDrawing ? (
              <motion.div
                key="drawing"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                className="space-y-4"
              >
                <div className="text-6xl mb-4 flex justify-center">
                  <RandomDrawIcon className="text-primary-200" aria-hidden="true" />
                </div>
                <div className="text-4xl font-bold text-primary-100 animate-pulse">Žrebuje sa skupina...</div>
                <div className="text-xl text-slate-300">
                  {activeGroup
                    ? `${activeGroup.title}: ${liveGroupPreview.draws.length}/${activeGroup.size}`
                    : 'Čakajte na výsledok'}
                </div>
              </motion.div>
            ) : displayGroup ? (
              <motion.div
                key="group-finished"
                initial={{ opacity: 0, scale: 0.8, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.8, y: -20 }}
                className="space-y-4"
              >
                <div className="text-6xl mb-4 flex justify-center">
                  <TrophyIcon className="text-primary-100" aria-hidden="true" />
                </div>
                <div className="text-3xl font-bold text-primary-50">
                  {isDisplayGroupInProgress ? 'Skupina prebieha' : 'Skupina dokončená'}
                </div>
                <div className="text-xl text-slate-200">
                  {`${displayGroup.title} • ${displayGroup.draws.length}/${displayGroup.size} žrebov`}
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="ready"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="space-y-4"
              >
                <div className="text-6xl mb-4 flex justify-center">
                  <BullseyeIcon className="text-primary-200" aria-hidden="true" />
                </div>
                <div className="text-4xl font-bold text-primary-100">Pripravené na žrebovanie</div>
                <div className="text-xl text-slate-300">
                  {nextGroup
                    ? `${nextGroup.title}: 0/${nextGroup.remainingInGroup} žrebov`
                    : 'Žrebovanie ukončené'}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="mt-6 pt-6 border-t border-slate-800/70">
            <div className="flex flex-col items-center justify-center mb-3 gap-1 text-center">
              <h3 className="text-lg font-semibold text-primary-100">Skupina</h3>
              <span className="text-sm text-slate-400">{previewGroup.title}</span>
            </div>
            <div className="flex flex-wrap justify-center gap-4">
              {Array.from({ length: getPreviewSlotsCount(previewGroup) }).map((_, idx) => {
                const draw = previewGroup.draws[idx];
                return (
                  <div
                    key={`inline-preview-${idx}`}
                    className="w-full sm:w-[calc(50%-0.5rem)] lg:w-[18rem] rounded-xl border border-slate-700 bg-slate-900/70 p-5 min-h-32 flex flex-col justify-center items-center text-center"
                  >
                    {draw ? (
                      <>
                        <div className="text-4xl md:text-5xl font-bold text-primary-100 leading-tight">
                          {draw.ticketNumber}
                        </div>
                        <div className="text-base text-slate-300 mt-1">Žreb #{draw.drawNumber}</div>
                        <div className="text-lg text-slate-200 mt-2 leading-snug">{draw.customerName}</div>
                      </>
                    ) : (
                      <div className="flex flex-col items-center gap-3 text-slate-500">
                        <span className="w-24 h-12 rounded-full border border-primary-400/60 bg-primary-500/20 text-primary-100 text-sm font-bold flex items-center justify-center">
                          {rotatingEligibleTickets.length > 0
                            ? rotatingEligibleTickets[(ticketRotateTick + idx) % rotatingEligibleTickets.length]
                            : '--'}
                        </span>
                        <div className="text-base">Čaká sa na žrebovanie</div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {isAdmin && (
        <div className="card bg-slate-900/80 backdrop-blur border border-slate-800/70">
          <h3 className="text-xl font-semibold text-primary-100 mb-4 flex items-center gap-2">
            <FiSettings className="text-primary-300" aria-hidden="true" />
            <span>Manuálne ovládanie</span>
          </h3>
          
          <div className="flex flex-wrap gap-4">
            <button
              onClick={handleDrawGroup}
              disabled={isDrawing || isStagingResults || eligibleTickets.length === 0 || !nextGroup}
              className="btn btn-primary flex items-center gap-2 text-lg px-8 py-4 disabled:opacity-50"
            >
              <FiPlay className="w-5 h-5" />
              {(isDrawing || isStagingResults)
                ? 'Žrebuje sa skupina...'
                : nextGroup
                  ? `Spustiť ${nextGroup.title} (${nextGroup.remainingInGroup} žrebov)`
                  : 'Žrebovanie ukončené'}
            </button>
          </div>
          
          {eligibleTickets.length === 0 && (
            <div className="mt-6 p-4 border border-warning-500/40 rounded-xl bg-warning-500/10">
              <p className="text-warning-200">
                <span className="inline-flex items-center gap-2">
                  <WarningIcon className="text-warning-300" aria-hidden="true" />
                  <span>Žiadne lístky nie sú dostupné na žrebovanie. Uistite sa, že existujú zaplatené objednávky.</span>
                </span>
              </p>
            </div>
          )}
        </div>
      )}

      {/* Draw History */}
      <div className="card bg-slate-900/80 backdrop-blur border border-slate-800/70">
        <div className="pb-4 mb-4 border-b border-slate-800/70">
          <h3 className="text-xl font-semibold text-primary-100 flex items-center gap-2">
            <TrophyIcon className="text-primary-300" aria-hidden="true" />
            <span>Skupiny žrebovania</span>
          </h3>
        </div>
        
        <div className="space-y-2">
          {visibleGroupedHistory.length > 0 ? (
            <div className="space-y-2">
              {visibleGroupedHistory.map((group, index) => (
                <motion.div
                  key={group.index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="p-3 sm:p-4 hover:bg-slate-800/60 rounded-xl transition-colors border border-slate-800/70"
                >
                  <div className="flex items-center justify-between gap-4 mb-3">
                    <div className="flex items-center gap-3">
                      <div className="min-w-12 h-12 px-3 rounded-full flex items-center justify-center font-bold text-primary-100 bg-primary-500/15 border border-primary-500/40">
                        {group.title}
                      </div>
                      <div>
                        <div className="font-semibold text-lg text-slate-100">
                          {group.draws.length} / {group.size} vyžrebovaných
                        </div>
                        <div className="text-slate-300">Skupina výhercov</div>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {group.draws.map((draw) => (
                      <div key={draw.id} className="rounded-xl border border-slate-700 bg-slate-900/70 p-4 md:p-5 min-h-28 text-center flex flex-col items-center justify-center">
                        <div className="text-lg md:text-xl font-bold text-primary-100">#{draw.drawNumber} • {draw.ticketNumber}</div>
                        <div className="text-base md:text-lg text-slate-200 mt-1">{draw.customerName}</div>
                        <div className="text-sm text-slate-500 mt-2">
                          {draw.drawnAt
                            ? new Date(draw.drawnAt).toLocaleString('sk-SK', {
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit'
                              })
                            : 'Neznámy dátum'}
                        </div>
                      </div>
                    ))}
                    {Array.from({ length: Math.max(group.size - group.draws.length, 0) }).map((_, emptyIndex) => (
                      <div key={`empty-${group.index}-${emptyIndex}`} className="rounded-xl border border-dashed border-slate-700/80 bg-slate-900/30 p-4 md:p-5 text-slate-500 min-h-28 flex items-center justify-center text-center">
                        <div className="flex flex-col items-center gap-3">
                          <span className="w-24 h-12 rounded-full border border-primary-400/60 bg-primary-500/20 text-primary-100 text-sm font-bold flex items-center justify-center">
                            {rotatingEligibleTickets.length > 0
                              ? rotatingEligibleTickets[
                                  (ticketRotateTick + group.index + emptyIndex) % rotatingEligibleTickets.length
                                ]
                              : '--'}
                          </span>
                          <span>Čaká sa na žrebovanie</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="text-right text-xs text-slate-500 mt-3">
                    {group.title}
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="text-6xl mb-4 flex justify-center">
                <BullseyeIcon className="text-primary-300" aria-hidden="true" />
              </div>
              <h4 className="text-xl font-semibold text-slate-100 mb-2">Žiadne žrebovania</h4>
              <p className="text-slate-400">Skupiny sa zobrazia po prvom žrebovaní.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DrawInterface;
